import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {combineLatestWith, of as observableOf} from 'rxjs';
import {map, switchMap, catchError, tap, filter, take} from 'rxjs/operators';

import {AuthActionTypes} from '../auth.actions';
import * as authAction from '@app/views/common/auth.actions';
import {State} from '@app/app.reducers';
import {AnnouncementService, EventNames, PolicyConfig} from './announcement.service';
import {DEADLINE_TO_SET_UP_MFA_FOR_VENDORS, User} from '@app/core/model/user';
import {environment} from '@env/environment';
import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';

@Injectable()
export class AnnouncementEffects {

    checkAuthAnnouncements$ = createEffect(() =>
            this.actions.pipe(
                ofType(AuthActionTypes.CHECK_ACCEPTED_PRIVACY_POLICY),
                map((action: authAction.AuthenticatedSuccessAction) => action.payload),
                tap(() => this.announcementService.isCheckingClaimsForAuthentication.next(true)),
                tap(() => this.announcementService.isClaimingAnnouncementsIsInProgress.next(true)),
                switchMap(payload =>
                    this.announcementService.checkClaimsForAuthentication()
                        .pipe(
                            combineLatestWith(this.authenticatedUserService.passwordExpirationPolicy$.pipe(take(1))),
                            map(([policyConfig, passwordExpirationPolicy]: [PolicyConfig, number]) => {
                                if (policyConfig && payload.user) {
                                    const currentUser: User = payload.user;
                                    let isClaimingAnnouncementsIsInProgress: boolean;

                                    if (currentUser && !currentUser.primaryAdmin && policyConfig.hasOrgLevelDocuments && !policyConfig.confirmedAllOrgLevelDocuments) {
                                        this.announcementService.onDialogEvents.next({
                                            type: 'extinfo',
                                            name: EventNames.Authentication,
                                            message: `Your ${environment.customer.customerShortName} account is now activated. However ${environment.customer.customerShortName} School Pledge is still not signed on behalf of your organization. Please invite authorized person to sign School Pledge to unlock functionality of ${environment.customer.appTitle}.`,
                                        });
                                        this.announcementService.allClaimAcceptedEvents.next(false);
                                        isClaimingAnnouncementsIsInProgress = true;

                                    } else {
                                        if (policyConfig.claims && policyConfig.claims.length > 0) {
                                            this.announcementService.onDialogEvents.next({
                                                type: 'announcement',
                                                name: EventNames.Authentication,
                                                message: null,
                                            });
                                            this.announcementService.allClaimAcceptedEvents.next((policyConfig.claims.length > 0 || !currentUser.primaryAdmin) && policyConfig.isAllAccepted());
                                            isClaimingAnnouncementsIsInProgress = true;

                                        } else {
                                            this.announcementService.allClaimAcceptedEvents.next(true);
                                            this.store.dispatch(new authAction.ShowContentAction());
                                            isClaimingAnnouncementsIsInProgress = false;
                                        }
                                    }

                                    if (passwordExpirationPolicy) {
                                        let showPasswordExpirationNotification: boolean;
                                        let passwordExpireDays: number;
                                        [showPasswordExpirationNotification, passwordExpireDays] = this.authenticatedUserService.checkPasswordExpiration(currentUser);

                                        if (showPasswordExpirationNotification && (passwordExpireDays > -1)) {
                                            this.announcementService.onDialogEvents.next({
                                                type: 'passwordExpireNotification',
                                                name: EventNames.Authentication,
                                                message: `Your password will expire
                                                          ${passwordExpireDays === 0 ? 'today' : (passwordExpireDays === 1 ? 'in ' + passwordExpireDays + ' day' : 'in ' + passwordExpireDays + ' days')}.
                                                          Do you want to update it now?`
                                            });
                                            this.announcementService.allClaimAcceptedEvents.next(false);
                                            isClaimingAnnouncementsIsInProgress = true;
                                        }
                                    }

                                    if (this.authenticatedUserService.isVendor(currentUser)
                                        && !currentUser?.mfaEnabled
                                        && Date.now() < new Date(DEADLINE_TO_SET_UP_MFA_FOR_VENDORS).getTime()) {

                                        this.announcementService.onDialogEvents.next({
                                            type: 'setUpMFAForVendorsDeadlineExpireNotification',
                                            name: EventNames.Authentication,
                                            message: `To improve security, set up multi-factor authentication by \<b\>February 19, 2025\</b\>. After that date, you'll be required to set up multi-factor authentication during sign in.`
                                        });
                                        this.announcementService.allClaimAcceptedEvents.next(false);
                                        isClaimingAnnouncementsIsInProgress = true;
                                    }

                                    this.announcementService.isClaimingAnnouncementsIsInProgress.next(isClaimingAnnouncementsIsInProgress);
                                }
                                this.announcementService.isCheckingClaimsForAuthentication.next(false);
                            }),
                            catchError(error => {
                                this.announcementService.isCheckingClaimsForAuthentication.next(false);
                                this.announcementService.isClaimingAnnouncementsIsInProgress.next(false);
                                this.store.dispatch(new authAction.SignOutAction());
                                return observableOf({});
                            })
                        )
                )
            ),
        {dispatch: false}
    );

    constructor(private actions: Actions,
                private announcementService: AnnouncementService,
                private authenticatedUserService: AuthenticatedUserService,
                private store: Store<State>) {
    }
}
